/**
 * Who plays the next round, and with whom.
 *
 * Two steps. First, who plays: the rest rule (sat out last round first), then games balance,
 * then the mix, then a latecomer's catch-up credit. Second, how they are arranged: Americano
 * shuffles and searches for the arrangement with no repeated partners and the widest spread of
 * opponents; Mexicano sorts by the board, cuts into fours, pairs 1st+4th against 2nd+3rd and only
 * moves people to avoid a repeated partnership, between neighbouring fours, the top four on court
 * 1. Both use one cost function and one local search.
 *
 * The promise (decision 36): nobody sits out three rounds running unless the numbers force it.
 * To keep the hall mixing, up to four people in any pair of rounds wait one extra round while
 * four others stay on, shared out so it is never the same people. Everyone here all day
 * finishes within one game of each other, a late group or not, 33 here or not: no swap for the
 * mix and no catch-up game is made that would leave two of them two games apart. Friends who
 * arrive together to full courts are split on arrival, and nobody new waits more than one round
 * for a first game. A catch-up game is given up by whoever going on has played most, never by
 * someone a game behind. Before the mix, exactly 32 here meant two fixed halves of 16 that never
 * met all day.
 *
 * The one exception is a wind down (decision 42), which an admin starts for the last rounds of
 * the day: then the promise above gives way to levelling everyone, and only for as long as it is
 * on. Who plays is whoever is furthest short of the plan's target (chooseWindDown); someone may
 * sit three rounds running, play several running, and courts may sit empty. The arrangement is
 * the same as ever: Americano spreads partners and opponents, Mexicano groups the chosen players
 * by the board. Cancel it and the next draw is the usual one.
 *
 * Everything is driven by a seeded random source, so the same state and seed draw the same
 * round. A different `attempt` gives the admin a fresh draw.
 */

import { makeRng, roundSeed, type Rng } from "./rng";
import { mainBoard } from "./ranking";
import { availablePlayers, history, pairKey, playersIn, type History } from "./selectors";
import type { CourtGame, Lineup, PlayerId, TournamentState } from "./types";
import { windDownNow } from "./winddown";

export const PARTNER_REPEAT_COST = 1000;
export const OPPONENT_REPEAT_COST = 10;
export const RANK_BALANCE_COST = 6;
export const RANK_SPREAD_COST = 20;
const AMERICANO_RESTARTS = 40;
const MAX_PASSES = 40;
/** The mix: the most people who stay on in place of a rested player, across any two rounds running. */
export const MIX_SWAPS = 4;

export interface DraftOptions {
  /** A fresh draw of the same round: the admin's "regenerate". */
  attempt?: number;
}

export interface Selection {
  playing: PlayerId[];
  catchUp: PlayerId[];
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((x, y) => x - y);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? (sorted[mid] as number) : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

/**
 * Who plays. `present` are the players who are "in" right now.
 *
 * Priority among those who did not play last round: anyone who has sat out the last two rounds
 * goes first (never a third if it can be helped), then anyone with no game yet (one who has
 * already waited a round ahead of one walking in now), then the lowest share of rounds played,
 * then whoever has sat out twice running most often, then chance. So when more have rested than
 * there are places, the one kept off has had the most play. Only if the rested do not fill the
 * courts does anyone play twice running, lowest share first.
 *
 * Then the mix, so the hall does not settle into two halves that never meet: up to MIX_SWAPS
 * people who just played stay on in place of people who rested. The allowance is for any two
 * rounds running, and whoever the numbers already sent on again, or kept off again, uses it up.
 * The one waiting has sat out one round, not two, is among those who have sat out twice running
 * least often, and has as many games as anyone going on. The one staying on has not played the
 * last two rounds, is level on games with the one waiting or one behind (further behind is what
 * catch-up games are for), and has no more games than anyone else resting who has been here as
 * long, give or take a round. So no swap leaves two people two games apart. Of the swaps allowed,
 * the one that puts the most new faces on court together is taken. Games are only level after an
 * even number of rounds, so with a steady hall the mix lands on every other round, from round 3.
 *
 * The test is games played, not share of rounds. Someone who taps in while round 1 is on is in
 * one round fewer than the first courts all day, so their share never matches: with 16 at the
 * start and 16 tapping in, a share test let no swap through and the hall was two halves again.
 *
 * Of a group arriving together, half (rounded down) wait one round, so they do not move through
 * the day as one block. The other half go on at once, and the half who waited go on the round
 * after. If the rested do not fill the courts, the waiting half take the empty places before
 * anyone plays twice running, so the split only bites when the courts would be full without them. With more than
 * 32 here there can be fewer empty places than arrivals held: one then sits while a swap keeps
 * someone on.
 *
 * A latecomer holding a catch-up credit and at least two games behind the room's median may take
 * one place, at most one such swap a round. The one who gives way has not waited two rounds and
 * has the most games of anyone going on, with as many as the room's median or more: first
 * someone who was about to play twice running, then a rested player (the fewest double sits,
 * then the seeded order). If nobody like that is going on, there is no catch-up game this round.
 * Nobody already a game behind the hall pays for someone else's catch-up.
 */
export function choosePlayers(state: TournamentState, present: PlayerId[], hist: History, rng: Rng): Selection {
  const per = state.config.playersPerCourt;
  const slots = Math.min(state.config.courts, Math.floor(present.length / per)) * per;
  if (slots === 0) return { playing: [], catchUp: [] };

  const games = (id: PlayerId): number => hist.gamesPlayed[id] ?? 0;
  const rounds = (id: PlayerId): number => hist.roundsPresent[id] ?? 0;
  const share = (id: PlayerId): number => (rounds(id) ? games(id) / rounds(id) : 0);
  // The round before the last one: with `hist`, enough to know who is on a run of two.
  const before = state.rounds[state.rounds.length - 2]?.lineup;
  const satBefore = new Set(before?.sittingOut ?? []);
  const playedBefore = new Set(before ? playersIn(before) : []);
  const satTwice = (id: PlayerId): boolean => hist.satOutLast.has(id) && satBefore.has(id);
  const playedTwice = (id: PlayerId): boolean => hist.playedLast.has(id) && playedBefore.has(id);
  // No game yet: 2 if they have also waited a round for it, 1 if they are walking in now.
  const fresh = (id: PlayerId): number => (games(id) > 0 ? 0 : hist.satOutLast.has(id) ? 2 : 1);
  // Rested players: anyone who has sat out the last two rounds comes first, then anyone with no
  // game yet (a day of double sits in the hall must not put a newcomer at the back), then the
  // lowest share, then the most double sits. Share goes ahead of double sits because, once the
  // mix has given nearly everyone a double sit, the count no longer says who is behind: with 33
  // here it kept someone a game down off court while people a game up went on. Players who just
  // played: lowest share only (the double-sit protection would otherwise hand them extra games,
  // which the first simulated day showed as three players on 15 games).
  const byPriority = (ids: PlayerId[], protectDoubleSits: boolean): PlayerId[] =>
    rng.shuffle([...ids]).sort((x, y) => {
      if (protectDoubleSits) {
        const running = Number(satTwice(y)) - Number(satTwice(x));
        if (running !== 0) return running;
        const first = fresh(y) - fresh(x);
        if (first !== 0) return first;
      }
      const s = share(x) - share(y);
      if (Math.abs(s) > 1e-9 || !protectDoubleSits) return s;
      return (hist.doubleSits[y] ?? 0) - (hist.doubleSits[x] ?? 0);
    });

  // Two or more walking in together once the day is going: half of them wait a round.
  let held: PlayerId[] = [];
  if (state.rounds.length > 0) {
    const arrived = rng.shuffle(present.filter((id) => rounds(id) === 0));
    if (arrived.length >= 2) held = arrived.slice(0, Math.floor(arrived.length / 2));
  }

  const rested = byPriority(present.filter((id) => !hist.playedLast.has(id) && !held.includes(id)), true);
  const played = byPriority(present.filter((id) => hist.playedLast.has(id)), false);

  // Empty places go to the arrivals who were to wait before anyone goes on twice running.
  let playing = rested.slice(0, slots);
  if (playing.length < slots) playing = playing.concat(held.slice(0, slots - playing.length));
  if (playing.length < slots) playing = playing.concat(played.slice(0, slots - playing.length));

  // The mix. What is left of the allowance once the numbers have had their say: those who played
  // the last two rounds, those the rest rule sends on again now, and rested players with no court
  // to go to. Never in round 1.
  const met = (x: PlayerId, y: PlayerId): number =>
    (hist.partners.get(pairKey(x, y)) ?? 0) + (hist.opponents.get(pairKey(x, y)) ?? 0);
  const onAgainLast = [...hist.playedLast].filter((id) => playedBefore.has(id)).length;
  const onAgainNow = playing.filter((id) => hist.playedLast.has(id)).length;
  const keptOff = Math.max(0, rested.length - slots);
  const allowance = state.rounds.length ? MIX_SWAPS - onAgainLast - onAgainNow - keptOff : 0;
  for (let n = 0; n < allowance; n += 1) {
    const most = Math.max(...playing.map(games));
    const sitting = present.filter((id) => !playing.includes(id));
    const candidates = playing.filter((id) => hist.satOutLast.has(id) && !satTwice(id));
    const fewest = Math.min(...candidates.map((id) => hist.doubleSits[id] ?? 0));
    // waits: among the fewest double sits, and with as many games as anyone going on
    const waiting = candidates.filter((id) => (hist.doubleSits[id] ?? 0) === fewest && games(id) >= most);
    // stays on: no more games than anyone resting who has been here as long, give or take a round
    const staying = played.filter(
      (id) =>
        !playing.includes(id) &&
        !playedTwice(id) &&
        sitting.every((s) => games(s) >= games(id) || rounds(s) < rounds(id) - 1),
    );
    let best: { on: PlayerId; off: PlayerId } | null = null;
    let bestGain = Number.NEGATIVE_INFINITY;
    for (const off of rng.shuffle(waiting)) {
      for (const on of rng.shuffle([...staying])) {
        // level with the one waiting or one game behind: further behind is what catch-up is for
        if (games(on) > games(off) || games(on) < games(off) - 1) continue;
        // pairs on court who have already met, partners or opponents, that this swap takes away
        let gain = 0;
        for (const other of playing) if (other !== off) gain += met(off, other) - met(on, other);
        if (gain > bestGain + 1e-9) {
          bestGain = gain;
          best = { on, off };
        }
      }
    }
    if (!best) break;
    const { on, off } = best;
    playing = playing.map((c) => (c === off ? on : c));
  }

  // A latecomer left out by the rest rule may take one place, once a round. It comes from whoever
  // going on has played most, and only if that is no fewer games than the room's median: the old
  // pick, the last rested player with no double sit all day, was often someone already a game
  // behind once the mix had given nearly everyone a double sit. Most of everyone going on, not
  // only of the rested: an 11 gave way while 12s played twice running and ended two games down.
  const catchUp: PlayerId[] = [];
  const mid = median(present.map(games));
  for (const id of played) {
    if (playing.includes(id)) continue;
    const credits = state.players[id]?.catchUpCredits ?? 0;
    const deficit = mid - games(id);
    if (credits < 1 || deficit < 2) continue;
    const pool = playing.filter((c) => !satTwice(c) && (hist.satOutLast.has(c) || hist.playedLast.has(c)));
    const most = Math.max(...playing.map(games));
    // ties: someone about to play twice running, then fewest double sits, then the later place
    // in the seeded line-up
    const victim = pool
      .filter((c) => games(c) === most && most >= mid)
      .reverse()
      .sort(
        (x, y) =>
          Number(hist.playedLast.has(y)) - Number(hist.playedLast.has(x)) ||
          (hist.doubleSits[x] ?? 0) - (hist.doubleSits[y] ?? 0),
      )[0];
    if (!victim) break;
    playing = playing.map((c) => (c === victim ? id : c));
    catchUp.push(id);
    break;
  }
  return { playing, catchUp };
}

/** Quads of four positions: a = (0,1), b = (2,3). */
function quadsOf(positions: PlayerId[]): PlayerId[][] {
  const quads: PlayerId[][] = [];
  for (let i = 0; i + 3 < positions.length; i += 4) quads.push(positions.slice(i, i + 4));
  return quads;
}

export function arrangementCost(
  positions: PlayerId[],
  hist: History,
  rank: Map<PlayerId, number> | null,
): number {
  let cost = 0;
  for (const q of quadsOf(positions)) {
    const [p0, p1, p2, p3] = q as [PlayerId, PlayerId, PlayerId, PlayerId];
    cost += ((hist.partners.get(pairKey(p0, p1)) ?? 0) + (hist.partners.get(pairKey(p2, p3)) ?? 0)) * PARTNER_REPEAT_COST;
    for (const x of [p0, p1]) {
      for (const y of [p2, p3]) {
        const met = hist.opponents.get(pairKey(x, y)) ?? 0;
        cost += met * met * OPPONENT_REPEAT_COST;
      }
    }
    if (rank) {
      const r = q.map((id) => rank.get(id) ?? 0) as [number, number, number, number];
      cost += Math.abs(r[0] + r[1] - (r[2] + r[3])) * RANK_BALANCE_COST;
      cost += (Math.max(...r) - Math.min(...r) - 3) * RANK_SPREAD_COST;
    }
  }
  return cost;
}

/**
 * First-improvement hill climb over swaps of two positions: any two, or only those `allowed`
 * says (Mexicano keeps its swaps near the board group that needs them).
 */
function improve(
  positions: PlayerId[],
  hist: History,
  rank: Map<PlayerId, number> | null,
  allowed?: (i: number, j: number) => boolean,
): number {
  let best = arrangementCost(positions, hist, rank);
  for (let pass = 0; pass < MAX_PASSES && best > 0; pass += 1) {
    let improved = false;
    for (let i = 0; i < positions.length; i += 1) {
      for (let j = i + 1; j < positions.length; j += 1) {
        if (allowed && !allowed(i, j)) continue;
        const a = positions[i] as PlayerId;
        const b = positions[j] as PlayerId;
        positions[i] = b;
        positions[j] = a;
        const cost = arrangementCost(positions, hist, rank);
        if (cost < best) {
          best = cost;
          improved = true;
        } else {
          positions[i] = a;
          positions[j] = b;
        }
      }
    }
    if (!improved) break;
  }
  return best;
}

/** How many partnerships in a set of positions are repeats of an earlier round. */
function repeatsIn(positions: PlayerId[], hist: History): number {
  let n = 0;
  for (const q of quadsOf(positions)) {
    if ((hist.partners.get(pairKey(q[0] as PlayerId, q[1] as PlayerId)) ?? 0) > 0) n += 1;
    if ((hist.partners.get(pairKey(q[2] as PlayerId, q[3] as PlayerId)) ?? 0) > 0) n += 1;
  }
  return n;
}

/**
 * Mexicano: the players due on court, best placed first, cut into fours. Each four is paired the
 * cheapest of its three ways, 1st+4th against 2nd+3rd unless a repeat (or much the same
 * opponents again) says otherwise. Only a four that still holds a repeated partnership trades
 * people, and only with the fours either side of it on the board. The top four are left alone
 * unless they hold a repeat themselves, or the four below them cannot lose theirs any other way.
 * If a repeat is still left that a search across every court would remove, that search wins: no
 * repeated partners stays the first rule. Then arrange() numbers the courts down the board.
 */
function arrangeMexicano(sorted: PlayerId[], hist: History, rank: Map<PlayerId, number>): PlayerId[] {
  const quads = quadsOf(sorted);
  const positions: PlayerId[] = [];
  const canonical: PlayerId[] = [];
  for (const q of quads) {
    const [r1, r2, r3, r4] = q as [PlayerId, PlayerId, PlayerId, PlayerId];
    canonical.push(r1, r4, r2, r3); // 1st + 4th against 2nd + 3rd
    let best: PlayerId[] = [r1, r4, r2, r3];
    let bestCost = arrangementCost(best, hist, rank);
    for (const other of [
      [r1, r2, r3, r4],
      [r1, r3, r2, r4],
    ]) {
      const cost = arrangementCost(other, hist, rank);
      if (cost < bestCost) {
        bestCost = cost;
        best = other;
      }
    }
    positions.push(...best);
  }
  const clashing = quads.map((_, k) => k).filter((k) => repeatsIn(positions.slice(k * 4, k * 4 + 4), hist) > 0);
  if (!clashing.length) return positions;
  // swaps only between people within one four of a clashing four on the board, and first without
  // touching the top four when they have no clash of their own (they are court 1)
  const near = (i: number, j: number): boolean =>
    clashing.some((k) => Math.abs(Math.floor(i / 4) - k) <= 1 && Math.abs(Math.floor(j / 4) - k) <= 1);
  if (!clashing.includes(0)) {
    const kept = [...positions];
    improve(kept, hist, rank, (i, j) => i >= 4 && near(i, j));
    if (repeatsIn(kept, hist) === 0) return kept;
  }
  improve(positions, hist, rank, near);
  const left = repeatsIn(positions, hist);
  if (left === 0) return positions;
  // the old search across every court, from the same start as ever
  improve(canonical, hist, rank);
  return repeatsIn(canonical, hist) < left ? canonical : positions;
}

/** The arrangement: who partners whom, who faces whom, on which court. */
export function arrange(state: TournamentState, playing: PlayerId[], hist: History, rng: Rng): CourtGame[] {
  const quads = Math.floor(playing.length / 4);
  if (quads === 0) return [];
  // a short round uses the lowest-numbered courts, so a spare court's tablet shows no game
  const courtNumbers = Array.from({ length: Math.min(quads, state.config.courts) }, (_, i) => i + 1);
  let positions: PlayerId[];
  let rank: Map<PlayerId, number> | null = null;

  if (state.mode === "mexicano") {
    const byRank = new Map<PlayerId, number>();
    mainBoard(state).forEach((row, i) => byRank.set(row.id, i));
    rank = byRank;
    const sorted = [...playing].sort((x, y) => (byRank.get(x) ?? 0) - (byRank.get(y) ?? 0));
    positions = arrangeMexicano(sorted, hist, byRank);
  } else {
    let bestCost = Number.POSITIVE_INFINITY;
    positions = [...playing];
    for (let restart = 0; restart < AMERICANO_RESTARTS && bestCost > 0; restart += 1) {
      const candidate = rng.shuffle([...playing]);
      const cost = improve(candidate, hist, null);
      if (cost < bestCost) {
        bestCost = cost;
        positions = candidate;
      }
    }
    rng.shuffle(courtNumbers);
  }

  // Mexicano: the group with the best-placed player on court 1, and so on down the board
  const groups = quadsOf(positions);
  const board = rank;
  if (board) {
    const top = (q: PlayerId[]): number => Math.min(...q.map((id) => board.get(id) ?? 0));
    groups.sort((x, y) => top(x) - top(y));
  }
  const courts = groups.map((q, i) => {
    const [p0, p1, p2, p3] = q as [PlayerId, PlayerId, PlayerId, PlayerId];
    return { court: courtNumbers[i] as number, a: [p0, p1] as [PlayerId, PlayerId], b: [p2, p3] as [PlayerId, PlayerId], eventId: null };
  });
  return courts.sort((x, y) => x.court - y.court);
}

/**
 * Who plays during a wind down (decision 42): as many as the plan gives this round, those still
 * furthest short of the target first. Then whoever rested last round, then the seeded order.
 * Taking the furthest short first is what makes the sum come out in the planned number of rounds
 * (winddown.ts). The rest rule, the mix and catch-up games give way to it until the games are
 * done; nobody is drawn once everyone is on the target.
 */
export function chooseWindDown(state: TournamentState, present: PlayerId[], hist: History, rng: Rng): Selection {
  const plan = windDownNow(state);
  const places = plan?.rounds[0] ?? 0;
  if (!plan || !places) return { playing: [], catchUp: [] };
  const need = (id: PlayerId): number => plan.need[id] ?? 0;
  const playing = rng
    .shuffle(present.filter((id) => need(id) > 0))
    .sort((x, y) => need(y) - need(x) || Number(hist.playedLast.has(x)) - Number(hist.playedLast.has(y)))
    .slice(0, places);
  return { playing, catchUp: [] };
}

/**
 * Who the day's rule keeps off court in round `roundNo` (Config.sitsOutFirst): the player of that
 * real name, while the round is one of the first ones. They are still drawn, as sitting out.
 */
export function keptOff(state: TournamentState, roundNo: number): PlayerId[] {
  const rule = state.config.sitsOutFirst;
  if (!rule || roundNo > rule.rounds) return [];
  const name = rule.name.trim().toLowerCase();
  return Object.values(state.players)
    .filter((p) => p.name.trim().toLowerCase() === name)
    .map((p) => p.id);
}

/** The next round's line-up from the state as it is now. */
export function draftRound(state: TournamentState, opts: DraftOptions = {}): Lineup {
  const roundNo = state.rounds.length + 1;
  const rng = makeRng(roundSeed(state.seed, roundNo, opts.attempt ?? 0));
  const off = new Set(keptOff(state, roundNo));
  const present = availablePlayers(state);
  const drawable = present.filter((id) => !off.has(id));
  const hist = history(state);
  const { playing, catchUp } = state.windDown ? chooseWindDown(state, drawable, hist, rng) : choosePlayers(state, drawable, hist, rng);
  const courts = arrange(state, playing, hist, rng);
  const onCourt = new Set(courts.flatMap((c) => [...c.a, ...c.b]));
  return {
    mode: state.mode,
    courts,
    sittingOut: present.filter((id) => !onCourt.has(id)),
    catchUp: catchUp.filter((id) => onCourt.has(id)),
  };
}

/** How many partnerships in a line-up are repeats of an earlier round. */
export function partnerRepeats(lineup: Lineup, hist: History): number {
  let repeats = 0;
  for (const c of lineup.courts) {
    if ((hist.partners.get(pairKey(c.a[0], c.a[1])) ?? 0) > 0) repeats += 1;
    if ((hist.partners.get(pairKey(c.b[0], c.b[1])) ?? 0) > 0) repeats += 1;
  }
  return repeats;
}
